import type { AppContext, AppProps, NextWebVitalsMetric } from 'next/app'
import { appWithTranslation } from 'next-i18next'
import { ConsentProvider } from 'utils/hooks/useConsentManager'

import { ErrorBoundary } from 'modules/utils/ErrorBoundary'
import { start } from 'utils/bugsnag'
import nextI18NextConfig from '../../next-i18next.config'
import App from 'next/app'

import { InternalAnalyticsProvider } from 'utils/internalAnalytics'
import { useFeatureFlag } from 'modules/optimizely/hooks/useFeatureFlag'
import { OptimizelyFeatureFlags } from 'models/Optimizely'

const IsMParticleEnabledSwitch = ({
  appEnv,
  mParticleApiKey,
  isMParticleEnabled,
  children,
}: {
  appEnv?: string
  mParticleApiKey?: string
  isMParticleEnabled: boolean
  children: React.ReactNode
}) => {
  return isMParticleEnabled && mParticleApiKey && appEnv ? (
    <InternalAnalyticsProvider appEnv={appEnv} mParticleApiKey={mParticleApiKey}>
      {children}
    </InternalAnalyticsProvider>
  ) : (
    <ConsentProvider>{children}</ConsentProvider>
  )
}

const isDevEnv =
  process.env.NEXT_PUBLIC_APP_ENV === 'local' || process.env.NEXT_PUBLIC_APP_APP_ENV === 'dev'

export function reportWebVitals(metric: NextWebVitalsMetric) {
  isDevEnv && console.info(metric)
}

// Initialize Bugsnag
start()

const MyApp = ({ Component, pageProps }: AppProps) => {
  // Get the mParticle feature flag from Optimizely context
  const isMParticleEnabled = useFeatureFlag(OptimizelyFeatureFlags.classy_mparticle).enabled

  return (
    <IsMParticleEnabledSwitch
      appEnv={process.env.APP_ENV}
      mParticleApiKey={process.env.NEXT_PUBLIC_MPARTICLE_API_KEY}
      isMParticleEnabled={isMParticleEnabled}
    >
      <ErrorBoundary>
        <Component {...pageProps} />
      </ErrorBoundary>
    </IsMParticleEnabledSwitch>
  )
}

MyApp.getInitialProps = async (context: AppContext) => {
  const ctx = await App.getInitialProps(context)

  return ctx
}

export default appWithTranslation(MyApp, nextI18NextConfig)
